import {
  Box,
  Container,
  Divider,
  Grid,
  Group,
  Loader,
  Overlay,
  Paper,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import {
  IconBarcode,
  IconClockHour3,
  IconMessageExclamation,
} from "@tabler/icons-react";

import { safeApiCall } from "api/common.service";
import { getDescription } from "api/product.service";
import { LBL_BACK_ORDER_ITEM } from "common/constants";
import { AddToCartControls } from "components/cart/AddToCart";
import ProductCards from "components/products/ProductCards";
import { SafeImage } from "components/SafeImage ";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useProductStore } from "stores/useProductStore";
import { useSessionStore } from "stores/userSessionStore";
import { ProductSummary } from "types/model.types";
import { getProductTotalPriceAsString } from "utils/helper";

interface ProductDetailProp {
  id: string;
  name: string;
  description: string;
  // Add other product fields as needed
}

const ProductDetails: React.FC = () => {
  const { productCode } = useParams<{ productCode: string }>();
  const [productDesc, setProductDesc] = useState<string>("");
  const allProducts = useProductStore((s) => s.allProducts);
  const [product, setProduct] = useState<ProductSummary | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const getTopSelling = useProductStore((state) => state.getTopSelling);
  const topSelling = getTopSelling();

  const getFeatured = useProductStore((state) => state.getFeatured);
  //const updateDescription = useProductStore((state) => state.updateDescription);
  const featured = getFeatured();
  const { isLoggedIn, priceLevel } = useSessionStore();

  const fetchProductDescription = async (id: string | number) => {
    try {
      const html = await safeApiCall(getDescription(id));
      if (html) {
        setProductDesc(html);
      }
    } catch (err) {
      console.error("Failed to fetch description:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!productCode) {
      setProductDesc("");
      return;
    }
    // Scroll to top when productCode changes
    window.scrollTo({ top: 0, behavior: "smooth" });

    const match = allProducts.items.find(
      (p) => p.productCode.toString() === productCode,
    );
    setProduct(match || null);
    //if (match && (match.productDesc ?? "").trim() === "" && match.id) {
    if (match && match.id) {
      setLoading(true);
      fetchProductDescription(match.id);
    }
  }, [productCode]);

  if (!productCode) return <div>No product code provided.</div>;
  if (!product)
    return (
      <div>
        No product found: {productCode} - {allProducts.items.length}
      </div>
    );

  return (
    <Container size="xl" py="md">
      <Grid gutter="md" align="stretch">
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Paper p="md" style={{ overflow: "hidden" }}>
            <Box pos="relative" h={360}>
              <SafeImage
                src={`/images/d/${product.detailedImageName}`}
                alt={product.title}
                height={360}
                fit="contain"
              />
              {product.soh === 0 && (
                <Overlay
                  color="rgba(0, 0, 0, 0.4)" // Soft semi-transparent dark overlay
                  blur={0}
                  center
                  radius="md"
                  zIndex={0}
                >
                  <Text
                    fw={900}
                    size="xl"
                    c="white"
                    style={{
                      textShadow: "0 0 8px rgba(0, 0, 0, 0.1)", // Adds separation from background
                    }}
                  >
                    currently not available
                  </Text>
                </Overlay>
              )}
              {Boolean(product.backorder) && (
                <Box
                  style={{
                    position: "absolute",
                    top: 118,
                    right: -42,
                    width: 230,
                    textAlign: "center",
                    backgroundColor: "#fa5252",
                    fontWeight: 600,
                    fontSize: "1.2rem",
                    color: "white",
                    padding: "8px 30px",
                    transform: "rotate(45deg)",
                    transformOrigin: "top right",
                    zIndex: 2,
                    boxShadow: "0 2px 6px rgba(0, 0, 0, 0.2)",
                    pointerEvents: "none",
                  }}
                >
                  {LBL_BACK_ORDER_ITEM}
                </Box>
              )}
            </Box>
          </Paper>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 8 }}>
          <Paper p="md" style={{ height: "100%" }}>
            <Box
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                height: "100%",
              }}
            >
              {/* 🔹 Row 1: Title, flags, and Price */}
              <Stack gap={2}>
                <Group justify="space-between" align="center">
                  <Title size="xl" style={{ fontSize: "2.3rem" }} order={4}>
                    {product.title}
                    <Text size="md" style={{ fontSize: "2.3rem" }} c="dimmed">
                      {` ${product.unitsCtn} / CTN`}
                    </Text>
                  </Title>
                </Group>
                <Group gap="xs" style={{ marginTop: "20px" }}>
                  <Text size="lg" c="red.7">
                    <IconBarcode size={24} /> {product.productCode}
                  </Text>
                  {(Boolean(product.shortDated) ||
                    Boolean(product.onRequest)) && (
                    <>
                      {Boolean(product.shortDated) && (
                        <Text size="lg" c="yellow.7">
                          <IconClockHour3 size={24} /> Short Dated
                        </Text>
                      )}
                      {Boolean(product.onRequest) && (
                        <Text size="lg" c="blue.6">
                          <IconMessageExclamation size={24} /> On Request
                        </Text>
                      )}
                    </>
                  )}
                </Group>
              </Stack>
              {isLoggedIn && (
                <>
                  <Box>
                    <Text
                      fw={900}
                      size="xl"
                      style={{ fontSize: "3.2rem", whiteSpace: "nowrap" }}
                    >
                      {getProductTotalPriceAsString(product, priceLevel)}
                    </Text>
                  </Box>
                </>
              )}
              {/* 🔸 Row 2: Bottom-right Cart Controls */}
              {isLoggedIn && (
                <Group justify="flex-end" mt="md">
                  <AddToCartControls product={product} size="md" />
                </Group>
              )}
              <Paper p="md">
                {loading ? (
                  <Box ta="center" py="xl">
                    <Loader size="sm" type="dots" />
                    <Text mt="sm" size="sm" c="dimmed">
                      Loading product description...
                    </Text>
                  </Box>
                ) : productDesc ? (
                  <Box
                    className="productDetails"
                    mt="md"
                    dangerouslySetInnerHTML={{ __html: productDesc }}
                  />
                ) : (
                  <Text mt="sm" size="sm" c="dimmed">
                    Loading product description...
                  </Text>
                )}
              </Paper>
            </Box>
          </Paper>
        </Grid.Col>
      </Grid>
      <Divider style={{ margin: "10px 0" }} />
      <ProductCards products={topSelling} title="top sellers" />
    </Container>
  );
};

export default ProductDetails;
